import { type FC } from 'react'
import { type DefaultProps, PieChart } from '@egw/components'
import { twMerge } from 'tailwind-merge'
import cx from 'classnames'
import { useTranslation } from '@egw/i18n'

export interface RiskReturnAssetAllocationProps extends DefaultProps {
  currentRiskTolerance?: {
    equityPercent: number
    fixedIncomePercent: number
    cashEquivalentPercent: number
  }
  chartsClassName?: string
}

export const RiskReturnAssetAllocation: FC<RiskReturnAssetAllocationProps> = ({
  // Common default properties for each component
  className,
  chartsClassName,
  id,
  testId,
  ariaLabel,
  currentRiskTolerance = { equityPercent: 0, fixedIncomePercent: 0, cashEquivalentPercent: 0 },
}) => {
  const { t } = useTranslation('features-enrollment')

  // Component base class names. Can be overriden by className property.
  const baseClass = cx('flex w-full')

  // Final class names
  const finalClasses = twMerge(baseClass, className)
  const chartsClasses = twMerge('w-full h-[100px]', chartsClassName)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      aria-hidden="true"
    >
      <div className="flex justify-between gap-2 w-full">
        <div className="flex flex-col w-[100px] items-center">
          <PieChart
            className={chartsClasses}
            segmentOne={currentRiskTolerance.equityPercent}
            segmentTwo={currentRiskTolerance.fixedIncomePercent}
            segmentThree={currentRiskTolerance.cashEquivalentPercent}
            highlightedSegment={1}
            fontSize={20}
            testId="equity-chart"
          />
          <p className="typo-primary-xs-semibold text-center max-w-28 mt-3xs" data-testid="equity-chart-text">
            {t('investments.risk-return.equity')}
          </p>
        </div>
        <div className="flex flex-col w-[100px] items-center">
          <PieChart
            className={chartsClasses}
            segmentOne={currentRiskTolerance.equityPercent}
            segmentTwo={currentRiskTolerance.fixedIncomePercent}
            segmentThree={currentRiskTolerance.cashEquivalentPercent}
            highlightedSegment={2}
            fontSize={20}
            testId="fixed-income-chart"
          />
          <p
            className="typo-primary-xs-semibold text-center whitespace-pre-wrap max-w-28 mt-3xs"
            data-testid="fixed-income-chart-text"
          >
            {t('investments.risk-return.fixed_income')}
          </p>
        </div>
        <div className="flex flex-col w-[100px] items-center">
          <PieChart
            className={chartsClasses}
            segmentOne={currentRiskTolerance.equityPercent}
            segmentTwo={currentRiskTolerance.fixedIncomePercent}
            segmentThree={currentRiskTolerance.cashEquivalentPercent}
            highlightedSegment={3}
            fontSize={20}
            testId="cash-equivalent-chart"
          />
          <p
            className="typo-primary-xs-semibold text-center whitespace-pre-wrap max-w-28 mt-3xs"
            data-testid="cash-equivalent-chart-text"
          >
            {t('investments.risk-return.cash_equivalent')}
          </p>
        </div>
      </div>
    </div>
  )
}
